import { Loading, Popover } from '@nextui-org/react';
import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonRole } from '../Button';
import FileUploader from '../FileUploader';
import Modal from '../Modal';
import {
  AvatarContainer,
  BodyText,
  EditAvatarButton,
  EditIcon,
  ImgPlaceholderContainer,
  Item,
  LoadingWrapper,
  StyledAvatar,
  StyledPopoverContent,
} from './AvatarUploader.styles';

export interface AvatarUploaderProps {
  avatarSize?: string;
  imageUrl?: string;
  onUploadFinish: (fileUrl: string) => void;
  avatarInitials: string;
  squared?: boolean;
  hideEditButton?: boolean;
  editButtonSquared?: boolean;
  editCallToAction?: string | ReactNode;
  backgroundColor?: string;
  fontSize?: string;
  openUploader?: boolean;
  ImgPlaceholder?: ReactNode;
}

const AvatarUploader = ({
  imageUrl,
  onUploadFinish,
  avatarSize = '10rem',
  avatarInitials,
  squared = true,
  hideEditButton,
  editButtonSquared = true,
  fontSize,
  openUploader = false,
  ImgPlaceholder,
}: AvatarUploaderProps) => {
  const { t } = useTranslation();

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploadingAvatar, setUploadingAvatar] = React.useState(false);
  const [isEditPopoverOpen, setIsEditPopoverOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const onAvatarClick = () => {
    setIsEditPopoverOpen(false);
    setTimeout(() => fileInputRef?.current.click(), 100);
  };

  const onAvatarRemove = () => {
    // Simulate a loading state
    setUploadingAvatar(true);
    onUploadFinish(''); // Empty string removes avatar
    setTimeout(() => setUploadingAvatar(false), 1000);

    setIsEditPopoverOpen(false);
    setIsDeleteModalOpen(false);
  };

  useEffect(() => {
    if (openUploader) {
      onAvatarClick();
    }
  }, [openUploader]);

  return (
    <>
      <FileUploader
        inputRef={fileInputRef}
        setLoading={setUploadingAvatar}
        onUploadFinish={(url) => {
          onUploadFinish(url);
        }}
        accept="image/*"
      />
      <AvatarContainer avatarSize={avatarSize}>
        {(ImgPlaceholder && !imageUrl) || uploadingAvatar ? (
          <ImgPlaceholderContainer>{ImgPlaceholder}</ImgPlaceholderContainer>
        ) : (
          <StyledAvatar
            fontSize={fontSize}
            squared={squared}
            text={!uploadingAvatar && avatarInitials}
            src={imageUrl}
          />
        )}
        {uploadingAvatar && (
          <LoadingWrapper>
            <Loading size="lg" />
          </LoadingWrapper>
        )}
      </AvatarContainer>
      {!hideEditButton &&
        (!imageUrl ? (
          <EditAvatarButton squared={editButtonSquared} icon={<EditIcon />} onClick={onAvatarClick} />
        ) : (
          <>
            <Popover
              isOpen={isEditPopoverOpen}
              onOpenChange={(open) => {
                setIsEditPopoverOpen(open);
              }}
              placement="bottom-left"
            >
              <Popover.Trigger>
                <EditAvatarButton squared={editButtonSquared} icon={<EditIcon />} />
              </Popover.Trigger>
              <StyledPopoverContent>
                <Item onClick={onAvatarClick}>{t('avatarUploader.changePhoto')}</Item>
                <Item
                  $isDestructive
                  onClick={() => {
                    setIsEditPopoverOpen(false);
                    setIsDeleteModalOpen(true);
                  }}
                >
                  {t('avatarUploader.removeCurrentPhoto')}
                </Item>
              </StyledPopoverContent>
            </Popover>
            <Modal
              open={isDeleteModalOpen}
              onClose={() => {
                setIsDeleteModalOpen(false);
              }}
              submitButtonTitle={t('avatarUploader.remove')}
              onSubmit={onAvatarRemove}
              buttonRole={ButtonRole.Destructive}
              title={t('avatarUploader.removePhoto')}
              desktopSubmitButtonWidth="100%"
              desktopCancelButtonWidth="100%"
              width="28rem"
              mobileTitleMarginBottom="0.5rem"
              mobileContainerPadding="0 0 1rem 1.5rem"
              mobileDisableInnerScroll
              mobileShowBorderTop
            >
              <BodyText>{t('avatarUploader.removeDisclaimer')}</BodyText>
            </Modal>
          </>
        ))}
    </>
  );
};

export default AvatarUploader;
